import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

import { NotificationContentDescription } from '@/features/notifications/components/notification-content-description';
import { NotificationIcon } from '@/features/notifications/components/notification-icon';
import { useNotificationNavigation } from '@/features/notifications/hooks/use-notification-navigation';
import { useNotificationRead } from '@/features/notifications/hooks/use-notification-read';
import { getNotificationsQueryKey } from '@/features/notifications/hooks/use-notifications';
import {
  Notification,
  NotificationContentType,
  NotificationType,
} from '@/features/notifications/notification-types';
import {
  formatGroupDate,
  getNotificationContentTypeString,
  groupNotifications,
} from '@/features/notifications/util/notification-util';
import { CustomTable } from '@/shared/components/table/table';
import {
  CustomTableRow,
  TableRowProps,
} from '@/shared/components/table/table-row';
import { Gradients } from '@/shared/components/theme/gradient';
import { TableBody } from '@/shared/components/ui/table';
import { cn } from '@/shared/utils/cn';

export const NotificationsTable = ({
  notifications,
}: {
  notifications: Notification[];
}) => {
  const groupedNotifications = groupNotifications(notifications);

  return (
    <div className="space-y-8">
      {groupedNotifications.map((group, index) => (
        <NotificationsTableSection
          key={index}
          notifications={group.notifications}
          date={group.date}
        />
      ))}
    </div>
  );
};

export const NotificationsTableSection = ({
  notifications,
  date: date,
}: {
  notifications: Notification[];
  date: string;
}) => {
  const queryClient = useQueryClient();
  const { handleNotificationClick } = useNotificationNavigation();

  const { mutate: markAsRead } = useNotificationRead({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getNotificationsQueryKey(notifications[0].org_id),
      });
    },
  });

  const tableRows: TableRowProps[] = notifications.map(
    (notification): TableRowProps => ({
      type: 'single',
      onClick: () => handleNotificationClick(notification),
      props: [
        {
          width: 'w-[20%]',
          value: {
            item: (
              <NotificationTypeInfo
                type={notification.type as NotificationType}
                contentType={
                  notification.content_type as NotificationContentType
                }
              />
            ),
            type: 'custom',
          },
        },
        {
          width: 'w-[80%]',
          value: {
            item: <NotificationContent notification={notification} />,
            type: 'custom',
          },
        },
      ],
    }),
  );
  return (
    <CustomTable simpleHeader={formatGroupDate(date)} dottedBorder>
      <TableBody>
        {tableRows.map((row, index) => (
          <CustomTableRow
            key={index}
            props={row.props}
            type="single"
            onClick={() => {
              const notification = notifications[index];
              const isRead = notification.is_read;
              if (!isRead) {
                markAsRead([notification.id]);
              }
              row.onClick?.();
            }}
            isFirstRow={index === 0}
            isLastRow={index === tableRows.length - 1}
          />
        ))}
      </TableBody>
    </CustomTable>
  );
};

const NotificationTypeInfo = ({
  type,
  contentType,
}: {
  type: NotificationType;
  contentType: NotificationContentType;
}) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <div className="size-4">
        <NotificationIcon type={type} contentType={contentType} />
      </div>
      <div>{getNotificationContentTypeString(contentType)}</div>
    </div>
  );
};

const NotificationContent = ({
  notification,
}: {
  notification: Notification;
}) => {
  return (
    <div className="flex w-full flex-row justify-between">
      <div>
        <NotificationContentDescription notification={notification} />
      </div>
      <div className="flex flex-row items-center gap-2">
        {format(new Date(notification.created_at!), 'dd. MMMM', {
          locale: de,
        })}
        {!notification.is_read && (
          <div className={cn(Gradients.greenGradient, 'size-2 rounded-full')} />
        )}
      </div>
    </div>
  );
};
